import { Check, ImagePlus, Loader2, RefreshCw, Sparkles, Trash2 } from 'lucide-react';
import { type ChangeEvent, useState } from 'react';
import { toast } from 'sonner';

import { adminClient, type IntegrationLogoConcept } from '@/api/adminClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { MAX_LOGO_BYTES, errorMessage, readFileAsDataUrl } from './builderModel';

/** The sizes Studio actually renders a logo at: catalog card, dialog, builder header. */
const PREVIEW_SIZES = [32, 40, 56] as const;

function LogoTile({ src, size, className }: { src: string; size: number; className?: string }) {
  return (
    <span
      className={cn('flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-white', className)}
      style={{ width: size, height: size, padding: Math.max(3, Math.round(size / 10)) }}
    >
      <img src={src} alt="" className="h-full w-full object-contain" />
    </span>
  );
}

function ConceptCard({
  concept,
  selected,
  disabled,
  onUse,
}: {
  concept: IntegrationLogoConcept;
  selected: boolean;
  disabled: boolean;
  onUse: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onUse}
      aria-pressed={selected}
      className={cn(
        'group flex flex-col gap-3 rounded-xl border bg-card p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        selected ? 'border-brand ring-1 ring-brand' : 'border-border hover:border-foreground/30',
      )}
    >
      <div className="flex items-end justify-center gap-3 rounded-lg bg-white px-3 py-4">
        <img src={concept.dataUrl} alt={concept.name} className="h-24 w-24" />
        <LogoTile src={concept.dataUrl} size={32} className="border-zinc-200" />
      </div>
      <div className="min-w-0 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium">{concept.name}</span>
          {selected ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-brand">
              <Check className="h-3.5 w-3.5" />
              In use
            </span>
          ) : (
            <span className="text-xs text-muted-foreground group-hover:text-foreground">Use this</span>
          )}
        </div>
        {concept.idea ? <p className="line-clamp-2 text-xs leading-5 text-muted-foreground">{concept.idea}</p> : null}
      </div>
    </button>
  );
}

/**
 * Upload a logo, or have Claude Opus draw three concepts in the RapidSOS dot
 * language and pick one. Concepts are 64×64 SVGs on a transparent
 * background; picking one only changes the unsaved Identity form.
 */
export function LogoDesigner({
  draftId,
  title,
  brandColor,
  category,
  poweredBy,
  value,
  onChange,
  readOnly,
}: {
  draftId: string;
  title: string;
  brandColor: string;
  category: string;
  poweredBy: string[];
  value: string;
  onChange: (dataUrl: string) => void;
  readOnly: boolean;
}) {
  const [concepts, setConcepts] = useState<IntegrationLogoConcept[]>([]);
  const [shownNames, setShownNames] = useState<string[]>([]);
  const [direction, setDirection] = useState('');
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const result = await adminClient.generateIntegrationLogos(draftId, {
        brandColor,
        category,
        poweredBy,
        direction: direction.trim() || undefined,
        avoid: shownNames.slice(-9),
      });
      setConcepts(result.concepts);
      setShownNames((names) => [...names, ...result.concepts.map((concept) => concept.name)]);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setGenerating(false);
    }
  };

  const onUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('Use a logo under 180 KB — an SVG or a small PNG works best.');
      return;
    }
    try {
      onChange(await readFileAsDataUrl(file));
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card/40 p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-semibold">Logo</div>
          <p className="mt-1 max-w-xl text-xs leading-5 text-muted-foreground">
            A square mark on a transparent background. Studio shows it at 32, 40 and 56 px on a white tile, so keep it bold and
            simple. Upload an SVG or PNG under 180 KB, or generate one in the RapidSOS style.
          </p>
        </div>
        {readOnly ? null : (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" asChild>
              <label className="cursor-pointer">
                <ImagePlus className="mr-2 h-3.5 w-3.5" />
                {value ? 'Replace' : 'Upload'}
                <input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" className="sr-only" onChange={(event) => void onUpload(event)} />
              </label>
            </Button>
            {value ? (
              <Button variant="ghost" size="sm" onClick={() => onChange('')} aria-label="Remove logo">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
        )}
      </div>

      <div className="mt-4 flex items-end gap-3">
        {value ? (
          PREVIEW_SIZES.map((size) => (
            <div key={size} className="flex flex-col items-center gap-1.5">
              <LogoTile src={value} size={size} />
              <span className="text-[10px] tabular-nums text-muted-foreground">{size}px</span>
            </div>
          ))
        ) : (
          <div
            className="flex h-14 w-14 items-center justify-center rounded-lg border border-border text-lg font-semibold text-white"
            style={{ backgroundColor: brandColor }}
          >
            {(title.trim()[0] || '?').toUpperCase()}
          </div>
        )}
      </div>

      {readOnly ? null : (
        <div className="mt-5 border-t border-border pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={direction}
              onChange={(event) => setDirection(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !generating) {
                  event.preventDefault();
                  void generate();
                }
              }}
              placeholder="Optional steer: a map pin, fewer dots, more like a signal"
              aria-label="Logo direction"
              maxLength={300}
              className="min-w-[16rem] flex-1"
            />
            <Button onClick={() => void generate()} disabled={generating}>
              {generating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : concepts.length ? (
                <RefreshCw className="mr-2 h-4 w-4" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {generating ? 'Drawing…' : concepts.length ? 'Regenerate' : 'Generate with AI'}
            </Button>
          </div>
          <p className="mt-2 text-[11px] leading-4 text-muted-foreground">
            Claude Opus draws three concepts from the title, description and brand colour. It takes about 20 seconds.
          </p>

          {generating || concepts.length ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {generating
                ? [0, 1, 2].map((index) => (
                    <div key={index} className="flex animate-pulse flex-col gap-3 rounded-xl border border-border p-3">
                      <div className="h-32 rounded-lg bg-muted" />
                      <div className="h-3 w-2/3 rounded bg-muted" />
                      <div className="h-3 w-full rounded bg-muted" />
                    </div>
                  ))
                : concepts.map((concept) => (
                    <ConceptCard
                      key={concept.id}
                      concept={concept}
                      selected={concept.dataUrl === value}
                      disabled={generating}
                      onUse={() => onChange(concept.dataUrl)}
                    />
                  ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
